import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../logo.svg';
import { apiFetch, parseJsonSafe, setAuth } from '../api/client';
import { getSafeNextPath } from '../auth/session';
import { isBetaHelpEnabled } from '../betaHelp';
import { SUPPORT_EMAIL_ADDRESS, SUPPORT_EMAIL_GUIDANCE } from '../copy/onboardingStatus';

function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const fromPath = location.state?.from?.pathname ?? '/dashboard';
  const query = new URLSearchParams(location.search);
  const logoutReason = query.get('reason');
  const notice = query.get('notice');
  const nextPath = getSafeNextPath(query.get('next'));
  const nextUserId = query.get('next_user_id');
  const reasonMessage =
    logoutReason === 'expired'
      ? 'Session expired — please log in again.'
      : logoutReason === 'unauthorized'
        ? 'Please log in again.'
        : '';
  const inviteAcceptedNotice =
    notice === 'invite_accepted' ? 'Invite accepted — you can now log in.' : '';
  const [showReasonMessage, setShowReasonMessage] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitInFlightRef.current) {
      return;
    }

    submitInFlightRef.current = true;
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = (await parseJsonSafe(response)) || {};

      if (!response.ok) {
        const message = data?.message || data?.detail || 'Login failed. Please try again.';
        throw new Error(message);
      }

      const token = data?.access_token;
      if (!token) {
        throw new Error('No access token returned from server.');
      }
      const user = data?.user || null;
      setAuth(token, user);
      const isSameUserRestore =
        nextPath && nextUserId != null && user?.id != null && String(user.id) === nextUserId;
      navigate(isSameUserRestore ? nextPath : fromPath, { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'Login failed. Please try again.');
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page page-shell auth-page">
      <div className="public-shell">
        <section className="card public-panel">
          <div className="public-panel-copy">
            <div className="public-brand">
              <img src={logo} alt="Sumrise Logo" />
              <span>Sumrise</span>
            </div>
            <p className="public-kicker">Invite-only beta</p>
            <h1>Welcome back</h1>
            <p>
              Sign in to continue into the adaptive learning workspace, exam runs, and staff tools
              already assigned to your account.
            </p>
          </div>
          <ul className="public-feature-list">
            <li>
              <strong>Invite-led access</strong>
              <p>Accounts are activated through staff-issued invites, not public signup.</p>
            </li>
            <li>
              <strong>Guardian safeguards</strong>
              <p>Under-18 student access remains blocked until guardian requirements are satisfied.</p>
            </li>
            <li>
              <strong>Role-aware workspace</strong>
              <p>Admins, tutors, parents, and students land in the route surface appropriate to their role.</p>
            </li>
          </ul>
        </section>

        <section className="card auth-card">
          <div className="auth-card-header">
            <p className="public-kicker">Sign in</p>
            <p className="muted-text">Please enter your details to sign in.</p>
          </div>

        {reasonMessage && showReasonMessage && (
          <div className="notice notice-info auth-link-row">
            <span>{reasonMessage}</span>
            <button
              type="button"
              aria-label="Dismiss notice"
              className="btn btn-tertiary auth-inline-dismiss"
              onClick={() => setShowReasonMessage(false)}
            >
              Close
            </button>
          </div>
        )}

        {inviteAcceptedNotice && (
          <div className="notice notice-info" style={{ marginBottom: 'var(--space-4)' }}>
            {inviteAcceptedNotice}
          </div>
        )}

        {errorMessage && (
          <div className="notice notice-error" style={{ marginBottom: 'var(--space-4)' }}>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="stack-md">
          <div className="stack-xs">
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div className="stack-xs">
            <div className="auth-link-row">
              <label htmlFor="password">Password</label>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary auth-action" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

          <div className="auth-card-footer">
            <p className="muted-text" style={{ fontSize: 'var(--text-sm)' }}>
              Access is invite-only during beta. Ask an admin or tutor to create your account.
            </p>
            <p className="muted-text" style={{ fontSize: 'var(--text-sm)' }}>
              Cannot log in? Email <a href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>{SUPPORT_EMAIL_ADDRESS}</a>.{' '}
              {SUPPORT_EMAIL_GUIDANCE}
            </p>
            {isBetaHelpEnabled() ? (
              <p className="muted-text" style={{ fontSize: 'var(--text-sm)' }}>
                New to the beta? <Link to="/beta-help">Read the beta help guide</Link>.
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default LoginPage;
